import { z } from 'zod';
import { AssistantFirstMessageMode, AssistantIdleMessage, AssistantLanguage, AssistantOptionMode, LlmModelCompatibility, LlmModelProvider, LlmModelType, StructuredOutputSchemaType, VoiceCallBackgroundSound, VoicePersonaProvider, } from './constants.js';
import { AssistantToolDefinitionSchema } from './tool.js';
/** The language model the assistant reasons with. */
export const AssistantLlmModelSchema = z.object({
    id: z.uuid(),
    provider: z.enum(LlmModelProvider),
    modelId: z.string().describe("The provider's own name for the model, such as gpt-4.1."),
    displayName: z.string(),
    modelType: z.enum(LlmModelType),
});
/** The synthesized voice the assistant speaks with. */
export const AssistantVoicePersonaSchema = z.object({
    id: z.uuid(),
    provider: z.enum(VoicePersonaProvider),
    personaId: z.string().describe("The provider's own name for the voice."),
    displayName: z.string(),
    llmModelCompatibility: z.enum(LlmModelCompatibility),
});
/** One value the assistant is asked to extract from the conversation once it ends. */
export const AssistantStructuredOutputSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    organizationId: z.uuid(),
    schema: z.object({
        type: z.enum(StructuredOutputSchemaType),
        description: z.string().describe('What the assistant is asked to look for.'),
    }),
});
/**
 * One version of an assistant's configuration: what it says, how it sounds, what it may do, and
 * when it gives up.
 *
 * A definition is the editable thing an assistant points at, not a frozen copy taken when a call
 * was placed. Reading the definition a call recorded says how that configuration reads now; it
 * does not establish what it said at the time.
 */
export const AssistantDefinitionSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    assistantId: z.uuid().describe('The assistant this configuration belongs to.'),
    prompt: z.string().describe('The system prompt the assistant follows for the whole call.'),
    firstMessage: z.string().describe('The greeting, when the assistant speaks first.'),
    firstMessageMode: z
        .enum(AssistantFirstMessageMode)
        .describe('Who speaks first, and whether the greeting is firstMessage verbatim or generated from the prompt.'),
    preAssistantMessage: z
        .string()
        .nullable()
        .describe('Played before the assistant takes the call, such as a recording disclosure.'),
    voicemailMessage: z
        .string()
        .nullable()
        .describe('Left when an outbound call reaches voicemail. Null leaves no message.'),
    language: z.enum(AssistantLanguage),
    llmModel: AssistantLlmModelSchema,
    modelTemperature: z.number(),
    voicePersona: AssistantVoicePersonaSchema,
    voiceSimilarityBoost: z.number(),
    voiceSpeed: z.number(),
    voiceStability: z.number(),
    enableSsmlParsing: z.enum(AssistantOptionMode).nullable(),
    emotionRecognition: z.enum(AssistantOptionMode).nullable(),
    backgroundDenoising: z.enum(AssistantOptionMode).nullable(),
    backgroundSound: z.enum(VoiceCallBackgroundSound).nullable(),
    startSpeakingSmartEndpointing: z
        .enum(AssistantOptionMode)
        .nullable()
        .describe('Whether the assistant predicts the caller has finished rather than only waiting.'),
    startSpeakingWaitSeconds: z
        .number()
        .describe('Silence after the caller stops before the assistant starts speaking.'),
    stopSpeakingNumberOfWords: z
        .number()
        .describe('How many words the caller must say over the assistant for it to stop.'),
    stopSpeakingVoiceSeconds: z
        .number()
        .describe('How long the caller must speak over the assistant for it to stop.'),
    stopSpeakingVoiceBackOffSeconds: z
        .number()
        .describe('How long the assistant stays quiet after being interrupted.'),
    idleMessages: z
        .array(z.enum(AssistantIdleMessage))
        .nullable()
        .describe('What the assistant says when the caller has gone quiet.'),
    idleMessageMaxSpokenCount: z
        .number()
        .describe('How many idle messages the assistant speaks at most. Reaching it stops the reminders; ' +
        'it does not end the call.'),
    idleTimeoutSeconds: z
        .number()
        .describe('Silence before an idle message is spoken. Hanging up on silence is a separate rule ' +
        'derived from this value with a floor of a minute, so it is always the longer of the two ' +
        'and is never this value multiplied by idleMessageMaxSpokenCount.'),
    maxCallDurationMilliseconds: z
        .number()
        .describe('How long a call may run before it is ended, whatever is being said.'),
    toolDefinitions: z
        .array(AssistantToolDefinitionSchema)
        .describe('The tools this configuration offers the assistant, including transfers.'),
    structuredOutputs: z
        .array(AssistantStructuredOutputSchema)
        .describe('What the assistant is asked to extract from the call once it ends.'),
});
//# sourceMappingURL=definition.js.map